import { activeDictLang } from "@/lib/i18n-dict";

/**
 * One source for the site origin and for the URLs structured data publishes.
 *
 * `SITE_URL` had been redeclared in four files, and the schema builders that
 * used it disagreed about locale: the FAQ schema prefixed its URLs with the
 * active locale while the blog and glossary schemas hardcoded the English one,
 * so nine locales published `@id`s that contradicted their own canonical tag.
 * Anything that puts a URL into JSON-LD should come through here.
 */
export const SITE_URL = import.meta.env.VITE_SITE_URL || "https://varsco.com";

/**
 * Absolute URL for an English route path, carrying the active locale's prefix.
 *
 * Safe to call from head(), which runs outside React: the locale comes from the
 * same per-request SSR bridge activeDict() uses.
 */
export function localizedUrl(path: string): string {
  const lang = activeDictLang() ?? "en";
  const prefix = lang === "en" ? "" : `/${lang}`;
  return `${SITE_URL}${prefix}${path === "/" ? "" : path}`;
}

/**
 * Absolute URL for an asset reference.
 *
 * Bundled imports resolve to root-relative paths like `/assets/x-abc123.webp`,
 * which are valid in HTML but not in JSON-LD — schema.org consumers have no
 * base to resolve them against, so a relative `image` reads as no image at all.
 * Already-absolute URLs (Odoo serves its own) pass through untouched.
 */
export function absoluteAssetUrl(path: string): string {
  if (/^https?:\/\//i.test(path) || path.startsWith("//")) return path;
  return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}
